import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { map } from 'rxjs';

import { ActivateUserRequest } from '@dto/request/activate-user-request';
import { LoginRequest } from '@dto/request/login-request';
import { ProcessPasswordResetRequest } from '@dto/request/process-password-reset-request';
import { RequestPasswordResetRequest } from '@dto/request/request-password-reset-request';
import { UserRegistrationRequest } from '@dto/request/user-registration-request';
import { GenericApiResponse } from '@dto/response/generic-api-response';
import { LoginResponse } from '@dto/response/login-response';
import { environment } from '@app/environments/environment';
import { Role } from '@models/role';
import { User } from '@models/user';
import { HeaderConfig, StorageCase } from '@utilities/coreStrings';

const INACTIVITY_LIMIT = 15 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll'] as const;
const ACTIVITY_THROTTLE = 1000;
const MAX_TIMEOUT = 2_147_483_647;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  readonly apiUrl = environment.apiUrl;
  readonly headers = new HeaderConfig();

  private readonly currentUserState = signal<User | null>(readStoredUser());
  readonly currentUser = this.currentUserState.asReadonly();

  private inactivityTimeout: ReturnType<typeof setTimeout> | null = null;
  private expirationTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastActivityAt = 0;

  constructor() {
    this.setupActivityListeners();

    if (this.loggedIn()) {
      this.setupExpirationTimer();
      this.resetInactivityTimer();
    }
  }

  authUser(user: LoginRequest) {
    return this.http.post<LoginResponse>(this.apiUrl + '/users/login', user,
      {headers: this.headers.headerNoAuth, observe: 'response'})
      .pipe(map(response => {
        const responseBody = response.body?.responseBody;
        if (!responseBody) {
          throw new Error('Login response did not include a user.');
        }

        const authenticatedUser: User = { ...responseBody };

        localStorage.setItem(StorageCase.currentUser, JSON.stringify(authenticatedUser));
        localStorage.setItem(StorageCase.token, response.headers.get('authorization') || '');

        this.currentUserState.set(authenticatedUser);
        this.resetInactivityTimer();
        this.setupExpirationTimer();
        return response.body;
      }));
  }

  loggedIn(): boolean {
    return !!localStorage.getItem(StorageCase.token);
  }

  getCurrentUser(): User | null {
    return this.currentUserState();
  }

  getToken(): string | null {
    return localStorage.getItem(StorageCase.token);
  }

  getClaims(): readonly Role[] {
    return this.currentUserState()?.roles ?? [];
  }

  hasAnyRole(roles: readonly string[]): boolean {
    if (roles.length === 0) {
      return true;
    }

    const held = new Set(
      this.getClaims()
        .map((role) => role.description?.trim().toLowerCase())
        .filter((description): description is string => !!description),
    );

    return roles.some((role) => held.has(role.trim().toLowerCase()));
  }

  getExpiration(): { exp?: number } | null {
    const token = this.getToken()?.replace(/^Bearer\s+/i, '').trim();
    const segments = token?.split('.') ?? [];
    if (segments.length < 2) {
      return null;
    }

    try {
      return JSON.parse(decodeBase64Url(segments[1]));
    } catch {
      return null;
    }
  }

  logout(): void {
    localStorage.removeItem(StorageCase.currentUser);
    localStorage.removeItem(StorageCase.token);
    this.currentUserState.set(null);
    this.clearInactivityTimer();
    this.clearExpirationTimer();
  }

  requestPasswordReset(request: RequestPasswordResetRequest) {
    request.channel = "SAVANT_WEB";
    return this.http.post<GenericApiResponse>(this.apiUrl + '/users/request-password-reset', request,
      {headers: this.headers.headerNoAuth});
  }

  completePasswordReset(request: ProcessPasswordResetRequest) {
    return this.http.post<GenericApiResponse>(this.apiUrl + '/users/process-password-reset', request,
      {headers: this.headers.headerNoAuth})
  }

  registerUser(request: UserRegistrationRequest) {
    return this.http.post<GenericApiResponse>(this.apiUrl + '/users/register', request,
      {headers: this.headers.headerNoAuth});
  }

  activateUserUsingOtp(request: ActivateUserRequest) {
    return this.http.post<GenericApiResponse>(this.apiUrl + '/users/activate-user', request,
      {headers: this.headers.headerNoAuth});
  }

  private endSession(): void {
    this.logout();
    void this.router.navigate(['/auth/login']);
  }

  private setupExpirationTimer(): void {
    this.clearExpirationTimer();

    const expiry = this.getExpiration()?.exp;
    if (typeof expiry !== 'number') {
      return;
    }

    const timeoutDuration = expiry * 1000 - Date.now();
    if (timeoutDuration <= 0) {
      this.endSession();
      return;
    }
    if (timeoutDuration > MAX_TIMEOUT) {
      return;
    }

    this.expirationTimeout = setTimeout(() => this.endSession(), timeoutDuration);
  }

  private clearExpirationTimer(): void {
    if (this.expirationTimeout) {
      clearTimeout(this.expirationTimeout);
      this.expirationTimeout = null;
    }
  }

  private setupActivityListeners(): void {
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, () => this.onUserActivity(), { passive: true });
    }
  }

  private onUserActivity(): void {
    if (!this.loggedIn()) {
      return;
    }

    const now = Date.now();
    if (now - this.lastActivityAt < ACTIVITY_THROTTLE) {
      return;
    }
    this.lastActivityAt = now;
    this.resetInactivityTimer();
  }

  private resetInactivityTimer(): void {
    this.clearInactivityTimer();
    this.inactivityTimeout = setTimeout(() => this.endSession(), INACTIVITY_LIMIT);
  }

  private clearInactivityTimer(): void {
    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout);
      this.inactivityTimeout = null;
    }
  }
}

function decodeBase64Url(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function readStoredUser(): User | null {
  const stored = localStorage.getItem(StorageCase.currentUser);
  if (!stored) {
    return null;
  }

  try {
    return JSON.parse(stored) as User;
  } catch {
    localStorage.removeItem(StorageCase.currentUser);
    return null;
  }
}
